import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import WechatLoginButton from "./WechatLoginButton";
import { authFetch, handleAuthError } from "@/utils/authErrors";

/**
 * 国内登录面板
 * 支持：手机号+密码、手机号+验证码、微信扫码
 */
interface DomesticLoginPanelProps {
  redirectTo?: string;
}

export default function DomesticLoginPanel({
  redirectTo = "/",
}: DomesticLoginPanelProps) {
  const { t } = useTranslation("login");
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [smsCode, setSmsCode] = useState("");

  const phoneRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const smsPhoneRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // 倒计时
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  // 发送验证码
  const sendCode = async () => {
    const phone = smsPhoneRef.current?.value.trim();
    if (!phone || !/^1[3-9]\d{9}$/.test(phone)) {
      toast.error(t("common.invalidPhone"));
      return;
    }

    try {
      const resp = await authFetch("/users/send_verification_code", {
        method: "POST",
        body: JSON.stringify({ phone }),
      });
      const data = await resp.json();
      if (data.success) {
        toast.success(t("domestic.codeSent"));
        setCountdown(60);
      } else {
        handleAuthError(
          data.error,
          t as (key: string) => string,
          "common.loginFailed",
        );
      }
    } catch {
      toast.error(t("common.networkError"));
    }
  };

  // 密码登录
  const handlePasswordLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const phone = phoneRef.current?.value.trim();
    const password = passwordRef.current?.value.trim();

    if (!phone || !/^1[3-9]\d{9}$/.test(phone)) {
      toast.error(t("common.invalidPhone"));
      return;
    }
    if (!password) {
      toast.error(t("domestic.passwordLabel"));
      return;
    }

    await submitLogin({
      login_type: "password",
      login: phone,
      password,
    });
  };

  // 验证码登录
  const handleSmsLogin = useCallback(
    async (e?: React.FormEvent<HTMLFormElement>) => {
      e?.preventDefault();
      const phone = smsPhoneRef.current?.value.trim();

      if (!phone || !/^1[3-9]\d{9}$/.test(phone)) {
        toast.error(t("common.invalidPhone"));
        smsPhoneRef.current?.focus();
        return;
      }
      if (!smsCode || smsCode.length !== 6) {
        toast.error(t("common.invalidCode"));
        return;
      }

      await submitLogin({
        login_type: "verification_code",
        phone,
        verification_code: smsCode,
      });
    },
    [smsCode, t],
  );

  // 验证码输入处理（支持自动提交）
  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setSmsCode(value);
  };

  // 验证码自动提交
  useEffect(() => {
    if (smsCode.length === 6 && !isLoading) {
      handleSmsLogin();
    }
  }, [smsCode, isLoading, handleSmsLogin]);

  // 提交登录
  const submitLogin = async (params: Record<string, string>) => {
    const formData = new FormData();
    const csrfToken =
      document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
        ?.content || "";

    formData.append("authenticity_token", csrfToken);
    formData.append("user[country_region]", "cn");
    formData.append("user[signup_channel]", "phone");

    Object.entries(params).forEach(([key, value]) => {
      formData.append(`user[${key}]`, value);
    });

    setIsLoading(true);

    try {
      const response = await fetch("/users/sign_in", {
        method: "POST",
        body: formData,
        headers: { Accept: "text/html" },
        credentials: "same-origin",
        redirect: "follow",
      });

      // Devise 登录成功会返回 302 重定向，fetch 会自动跟随
      const isLoginPage =
        response.url.includes("/login") ||
        response.url.includes("/sign_in") ||
        response.url.includes("/users/sign_in");

      if (response.ok && !isLoginPage) {
        toast.success(t("common.loginSuccess"));
        setTimeout(() => {
          window.location.href = redirectTo;
        }, 300);
        // Do not set isLoading to false here to prevent double clicks
      } else {
        setIsLoading(false);
        handleAuthError(
          { code: "INVALID_CREDENTIALS" },
          t as (key: string) => string,
          "common.phoneOrPasswordError",
        );
      }
    } catch {
      setIsLoading(false);
      toast.error(t("common.networkError"));
    }
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="password" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="password">
            {t("domestic.passwordLogin")}
          </TabsTrigger>
          <TabsTrigger value="sms">{t("domestic.smsLogin")}</TabsTrigger>
        </TabsList>

        {/* 密码登录 */}
        <TabsContent value="password" className="mt-4">
          <form
            onSubmit={handlePasswordLogin}
            className="space-y-4"
            aria-label={t("domestic.passwordLogin")}
            aria-busy={isLoading}
          >
            <div>
              <Input
                id="phone"
                type="tel"
                placeholder={t("domestic.phonePlaceholder")}
                ref={phoneRef}
                autoComplete="tel"
                aria-required="true"
                aria-label={t("domestic.phoneLabel")}
                className="bg-background dark:bg-background"
              />
            </div>

            <div className="space-y-2">
              <Input
                id="password"
                type="password"
                placeholder={t("domestic.passwordPlaceholder")}
                ref={passwordRef}
                autoComplete="current-password"
                aria-required="true"
                aria-label={t("domestic.passwordLabel")}
                className="bg-background dark:bg-background"
              />
              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t("domestic.forgotPassword")}
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
              aria-busy={isLoading}
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isLoading ? t("domestic.loggingIn") : t("domestic.loginButton")}
            </Button>
          </form>
        </TabsContent>

        {/* 验证码登录 */}
        <TabsContent value="sms" className="mt-4">
          <form
            ref={formRef}
            onSubmit={handleSmsLogin}
            className="space-y-4"
            aria-label={t("domestic.smsLogin")}
            aria-busy={isLoading}
          >
            <div className="flex gap-2">
              <Input
                id="sms-phone"
                type="tel"
                placeholder={t("domestic.phonePlaceholder")}
                ref={smsPhoneRef}
                autoComplete="tel"
                aria-required="true"
                aria-label={t("domestic.phoneLabel")}
                className="flex-1 bg-background dark:bg-background h-10"
              />
              <Button
                type="button"
                variant="outline"
                disabled={countdown > 0 || isLoading}
                onClick={sendCode}
                className="shrink-0 h-10"
                aria-label={
                  countdown > 0
                    ? String(
                        (t as any)("domestic.resendIn", { seconds: countdown }),
                      )
                    : String(t("domestic.sendCode"))
                }
              >
                {countdown > 0 ? `${countdown}s` : t("domestic.sendCode")}
              </Button>
            </div>

            <div>
              <Input
                id="code"
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder={t("domestic.codePlaceholder")}
                value={smsCode}
                onChange={handleCodeChange}
                autoComplete="one-time-code"
                aria-required="true"
                aria-label={t("domestic.codeLabel")}
                className="bg-background dark:bg-background h-10"
              />
            </div>

            <p
              className="text-xs text-muted-foreground text-center"
              aria-live="polite"
            >
              {t("domestic.autoRegisterHint")}
            </p>

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
              aria-busy={isLoading}
              size="lg"
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isLoading
                ? t("domestic.loggingIn")
                : t("domestic.loginRegisterButton")}
            </Button>
          </form>
        </TabsContent>
      </Tabs>

      {/* 分隔线 */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="px-2 text-muted-foreground">
            {t("domestic.orDivider")}
          </span>
        </div>
      </div>

      {/* 微信登录 - 使用优化后的组件 */}
      <WechatLoginButton redirectTo={redirectTo} className="h-10" />
    </div>
  );
}
